// frontend/src/components/ExpressionEditor/handleExpressionCanvasDragEnd.ts
import { functionRegistry } from '../../utils/functionRegistry';
import { getEmittedFields, getDefaultFieldForIndicator } from '../../utils/indicatorDisplayRegistry';
import { v4 as uuidv4 } from 'uuid';
import type { ExpressionNode } from '../../state/types';

interface DragEndContext {
  rootRef: React.RefObject<HTMLDivElement>;
  availableSeries: any[];
  instruments: any[];
  indicators: any[];
  expressionGraph: any;
  setExpressionGraph: (updater: any) => void;
  setDialogSeriesLabel: (label: string) => void;
  setDialogIsOHLC: (isOHLC: boolean) => void;
  setDialogTargetSlotId: (id: string | null) => void;
  setDialogSeriesId: (id: string | null) => void;
  setDialogOpen: (open: boolean) => void;
  setDialogFunctionId: (id: string | null) => void;
  setDialogExistingTypes: (types: string[]) => void;
  setDialogExpectedType: (type: 'number' | 'string' | null) => void;
  setCompactDialog: (value: boolean) => void;
  setDialogAvailableFields: (fields: string[]) => void;
  setDialogDefaultField: (field: string | null) => void;
  showToast: (msg: string) => void;
}

export const handleExpressionCanvasDragEnd = (
  event: any,
  ctx: DragEndContext
) => {
  const { active, over } = event;
  if (!over) return;

  const activeType = active.data.current?.type;
  const overId = over.id;

  // === 1. FUNCTION DROP (canvas or slot) ===
  if (activeType === 'function') {
    const functionId = active.id;
    const def = functionRegistry.get(functionId);
    if (!def) return;

    const newId = `fn-${uuidv4()}`;
    const operandIds: (string | null)[] = [];
    const extraNodes: Record<string, ExpressionNode> = {};

    // Build operandIds + auto-create default literals
    def.parameters.forEach((param, idx) => {
      if (param.type === 'literal' && param.defaultValue !== undefined) {
        const litId = `lit-${uuidv4()}`;
        operandIds[idx] = litId;
        extraNodes[litId] = {
          id: litId,
          type: 'literal',
          value: param.defaultValue as number,
        };
      } else {
        operandIds[idx] = null;
      }
    });

    const newNode: ExpressionNode = {
      id: newId,
      type: 'function',
      functionId,
      operandIds,
      epsilon: undefined,
    };

    if (overId === 'expression-canvas') {
      // Drop on canvas → new root
      ctx.setExpressionGraph((prev: any) => ({
        rootId: newId,
        expressions: {
          ...prev.expressions,
          [newId]: newNode,
          ...extraNodes,
        },
      }));
    } else if (overId?.includes('-slot-')) {
      // Drop into existing function
      const match = overId.match(/^(.+)-slot-(\d+)$/);
      if (!match) return;
      const parentId = match[1];
      const slotIndex = parseInt(match[2], 10);

      ctx.setExpressionGraph((prev: any) => {
        const parent = prev.expressions[parentId];
        if (!parent || parent.type !== 'function') return prev;

        const newOperandIds = [...parent.operandIds];
        while (newOperandIds.length <= slotIndex) newOperandIds.push(null);
        newOperandIds[slotIndex] = newId;

        return {
          ...prev,
          expressions: {
            ...prev.expressions,
            [newId]: newNode,
            ...extraNodes,
            [parentId]: { ...parent, operandIds: newOperandIds },
          },
        };
      });
    }
    return;
  }

  // === 2. SERIES DROP ON SLOT ===
  if (activeType === 'series' && overId?.includes('-slot-')) {
    const seriesId = active.id;
    const match = overId.match(/^(.+)-slot-(\d+)$/);
    if (!match) return;
    const parentId = match[1];
    const slotIndex = parseInt(match[2], 10);

    const parentNode = ctx.expressionGraph.expressions[parentId];
    if (!parentNode || parentNode.type !== 'function') return;

    const def = functionRegistry.get(parentNode.functionId);
    const param = def?.parameters[slotIndex];

    // CASE A: seriesAware or seriesAwareWindow parameter — open compact dialog
    if (param?.type === 'seriesAware' || param?.type === 'seriesAwareWindow') {
      const isInstrument = ctx.instruments.some((i: any) => i.id === seriesId);
      const symbol = isInstrument
        ? ctx.instruments.find((i: any) => i.id === seriesId)?.symbol ?? 'Unknown'
        : (() => {
            const ind = ctx.indicators.find((i: any) => i.id === seriesId);
            return ind ? ind.name : 'Unknown';
          })();

      // Force compact mode
      ctx.setDialogSeriesLabel(symbol);
      ctx.setDialogIsOHLC(isInstrument);
      ctx.setDialogTargetSlotId(overId);
      ctx.setDialogSeriesId(seriesId);
      ctx.setCompactDialog(true);  // ← THIS IS THE MISSING PIECED
      ctx.setDialogOpen(true);

      return;
    }

    // CASE B: Regular series drop → open dialog with type awareness
    const isOHLC = ctx.instruments.some((i: any) => i.id === seriesId);
    const label = isOHLC
      ? ctx.instruments.find((i: any) => i.id === seriesId)?.symbol
      : (() => {
          const ind = ctx.indicators.find((i: any) => i.id === seriesId);
          return ind ? ind.name : 'Unknown';
        })() || 'Unknown';

    // Determine expected type from the target parameter
    let expectedType: 'number' | 'string' | null = null;
    if (param?.type === 'seriesDate') {
      expectedType = 'string'; // date-only: dialog shows only date field
    } else if (param?.type === 'series') {
      const funcDef = functionRegistry.get(parentNode.functionId);
      expectedType = funcDef?.returnType === 'string' ? 'string' : 'number';
    }

    ctx.setDialogSeriesLabel(label);
    ctx.setDialogIsOHLC(isOHLC);
    ctx.setDialogTargetSlotId(overId);
    ctx.setDialogSeriesId(seriesId);
    ctx.setDialogExpectedType(expectedType);
    if (isOHLC) {
      ctx.setDialogAvailableFields([]);
      ctx.setDialogDefaultField(null);
    } else {
      const ind = ctx.indicators.find((i: any) => i.id === seriesId);
      ctx.setDialogAvailableFields(ind ? Object.keys(getEmittedFields(ind.type)) : []);
      ctx.setDialogDefaultField(ind ? getDefaultFieldForIndicator(ind.type) ?? null : null);
    }
    ctx.setCompactDialog(false);
    ctx.setDialogOpen(true);
    return;
  }

  // === 3. CONSTANT DROP ON SLOT ===
  if (activeType === 'constant' && overId?.includes('-slot-')) {
    const constantId = active.data.current?.constantId;
    if (!constantId) return;
    const slotMatch = overId.match(/^(.+)-slot-(\d+)$/);
    if (!slotMatch) return;

    const parentId = slotMatch[1];
    const operandIndex = parseInt(slotMatch[2], 10);
    const newId = `const-${uuidv4()}`;

    const newNode: ExpressionNode = {
      id: newId,
      type: 'constant',
      constantId,
    };

    ctx.setExpressionGraph((prev: any) => {
      const parent = prev.expressions[parentId];
      if (!parent || parent.type !== 'function') return prev;

      const updatedParent: ExpressionNode = {
        ...parent,
        operandIds: parent.operandIds.map((id: string | null, idx: number) =>
          idx === operandIndex ? newId : id
        ),
      };

      return {
        ...prev,
        expressions: {
          ...prev.expressions,
          [newId]: newNode,
          [parentId]: updatedParent,
        },
      };
    });

    return;
  }

  // === 4. LITERAL DROP ===
  if (activeType === 'literal' && overId?.includes('-slot-')) {
    const literalValue = active.data.current.value;
    const slotMatch = overId.match(/^(.+)-slot-(\d+)$/);
    if (!slotMatch) return;

    const parentId = slotMatch[1];
    const operandIndex = parseInt(slotMatch[2], 10);
    const newId = `lit-${uuidv4()}`;

    const newNode: ExpressionNode = {
      id: newId,
      type: 'literal',
      value: literalValue,
    };

    ctx.setExpressionGraph((prev: any) => {
      const parent = prev.expressions[parentId];
      if (!parent || parent.type !== 'function') return prev;

      const updatedParent: ExpressionNode = {
        ...parent,
        operandIds: parent.operandIds.map((id: string | null, idx: number) => (idx === operandIndex ? newId : id)),
      };

      return {
        ...prev,
        expressions: {
          ...prev.expressions,
          [newId]: newNode,
          [parentId]: updatedParent,
        },
      };
    });

    return;
  }
};